/**
 * 经纪委托列表（我发布的 / 可接单的 / 已成交 / 已取消 / 已超时）
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { acceptBrokerOrder, cancelBrokerOrder, listBrokerOrders, type BrokerOrderView } from '@/api/broker';
import { useAuth } from '@/stores/auth';
import type { BrokerOrder } from '@/types';
import { BULK_THRESHOLD } from '@/utils/constants';
import { formatDateTime, formatMoney } from '@/utils/format';
import StatusTag from '@/components/common/StatusTag';
import Countdown from '@/components/common/Countdown';
import Loading from '@/components/common/Loading';
import ErrorBox from '@/components/common/ErrorBox';
import Empty from '@/components/common/Empty';
import PageHero from '@/components/common/PageHero';
import '@/styles/pages/broker.css';

const TABS: { key: BrokerOrderView; label: string }[] = [
  { key: 'mine', label: '我发布的' },
  { key: 'available', label: '可接单的' },
  { key: 'matched', label: '已成交' },
  { key: 'cancelled', label: '已取消' },
  { key: 'expired', label: '已超时' },
];

export default function BrokerOrders() {
  const { user } = useAuth();
  const [tab, setTab] = useState<BrokerOrderView>('mine');
  const [list, setList] = useState<BrokerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listBrokerOrders(tab);
      setList(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const isBroker = user?.roles?.includes('broker');

  const handleAccept = async (id: number) => {
    setBusyId(id);
    try {
      await acceptBrokerOrder(id);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (id: number) => {
    if (!window.confirm('确定取消该委托吗？')) return;
    setBusyId(id);
    try {
      await cancelBrokerOrder(id);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="broker-page">
      <PageHero eyebrow="BROKER ORDERS" title="经纪委托" desc="委托大厅 · 供需对接 · 撮合成交" />
      <div className="container">
        <div className="tabs">
          {TABS.map((t) => (
            <button key={t.key} className={`tab-item ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
          <Link className="tab-item broker-new-link" to="/broker/orders/new">＋ 发布委托</Link>
        </div>

        {loading ? (
          <Loading rows={5} type="list" />
        ) : error ? (
          <ErrorBox message={error} onRetry={load} />
        ) : list.length === 0 ? (
          <Empty
            icon="🤝"
            title="暂无委托"
            desc={tab === 'available' ? '当前没有可接单的委托' : '去发布一条委托吧'}
            action={<Link className="btn btn-primary btn-sm" to="/broker/orders/new">发布委托</Link>}
          />
        ) : (
          <div className="broker-list">
            {list.map((o) => (
              <div className="broker-card" key={o.id}>
                <div className="broker-card__head">
                  <StatusTag type="broker" status={o.status} />
                  <span className={`broker-card__type ${o.order_type === 1 ? 'buy' : 'sell'}`}>
                    {o.order_type === 1 ? '求购' : '求售'}
                  </span>
                  <span className="broker-card__no">{o.order_no}</span>
                  {o.expired_at && o.status === 0 ? (
                    <Countdown endTime={o.expired_at} prefix="可接剩余" expiredText="已超时" />
                  ) : null}
                </div>
                <div className="broker-card__body">
                  <div className="broker-card__meta">
                    <span>品种：<b>{o.cattle_type ?? '-'}</b></span>
                    <span>数量：<b>{o.quantity} 头</b>{o.quantity >= BULK_THRESHOLD ? <em>（大宗面议）</em> : null}</span>
                    <span>价格区间：<b>{formatMoney(o.price_min)} ~ {formatMoney(o.price_max)}</b></span>
                    <span>佣金：<b>{(o.commission_rate ?? 0.02) * 100}%</b></span>
                    <span>发布：<b>{formatDateTime(o.created_at)}</b></span>
                  </div>
                  {o.note ? <div className="broker-card__remark">备注：{o.note}</div> : null}
                  <div className="broker-card__publisher">
                    发布方：{o.publisher_name ?? `用户${o.publisher_id}`}
                    {o.broker_name ? ` · 经纪：${o.broker_name}` : ''}
                  </div>
                </div>
                <div className="broker-card__ops">
                  {tab === 'available' && isBroker ? (
                    <button className="btn btn-gold btn-sm" disabled={busyId === o.id} onClick={() => handleAccept(o.id)}>
                      {busyId === o.id ? '接单中…' : '接单'}
                    </button>
                  ) : null}
                  {tab === 'mine' && o.status === 0 ? (
                    <button className="btn btn-outline btn-sm" disabled={busyId === o.id} onClick={() => handleCancel(o.id)}>
                      取消委托
                    </button>
                  ) : null}
                  {tab === 'matched' && o.status === 2 && o.linked_order_id ? (
                    <Link className="btn btn-outline btn-sm" to={`/orders/${o.linked_order_id}`}>
                      查看关联订单
                    </Link>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
